import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';

// 개별 훅들 import
import { useAuth } from '../useAuth';
import { useVideo } from '../useVideo';
import { useTranscript } from '../useContent';
import { useQA } from '../useQA';
import { useResumeProcessing } from '../useAnalysis';
import { useGlobalCopy } from '../useModal';
import { useSwipeHandler } from '../useUI';
import { useAnalysisDetection } from '../useAnalysis';
import { useHistorySync } from '../useHistory';
import { useTranslationToggle } from '../useContent';
import { useHeaderAnalyze } from '../useUI';
import { usePageGuard } from '../useUI';
import { useComputedStates } from '../useUI';
import { useBackgroundReconnect } from '../useVideo/useBackgroundReconnect';


export const useAnalyzePageHooks = () => {
  // 기본 설정
  const { historyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const { t, i18n } = useTranslation();
  const isMobile = !useMediaQuery(theme.breakpoints.up('md'));

  // 🚨 백그라운드 완료 처리 플래그 (무한 루프 방지)
  const [isCompletionHandled, setIsCompletionHandled] = useState(false);

  // 1. 인증 관련
  const auth = useAuth();
  const { userId } = auth;

  // 2. 비디오 관련 (userId 필요)
  const video = useVideo(userId);

  // 3-1. 백그라운드 재접속 확인 (historySync보다 먼저 정의)
  const backgroundReconnect = useBackgroundReconnect(
    userId,
    historyId  // videoId 대신 historyId 직접 사용
  );

  // 3. 히스토리 동기화 (video 결과 필요)
  const historySync = useHistorySync(
    historyId, 
    video.history, 
    video.loading, 
    video.isStreaming,
    video.setAnalyzedVideoId,
    video.setAnalyzedTitle,
    video.setAnalyzedUrl,
    video.setSelectedIdx,
    backgroundReconnect,
    userId
  );

  // 4. 번역 토글 (historySync 결과 필요)
  const translation = useTranslationToggle(
    historySync.selectedHistory, 
    video.isStreaming, 
    video.streamingTranscript
  );

  // 5. 계산된 상태들
  const computed = useComputedStates(video, translation, historySync, isMobile);

  // 6. 자막 처리 (translation 결과 필요)
  const transcript = useTranscript(translation.currentTranscript, video.playerRef);

  // 7. QA 처리 (historySync 결과 필요)
  const qa = useQA(historySync.selectedHistory);

  // 8. 분석 관련 훅들
  const analysis = useAnalysisDetection(
    historyId, 
    video.history, 
    video.isStreaming, 
    video.setProgressModal, 
    video.startAnalysis, 
    t,
    backgroundReconnect
  );

  const resume = useResumeProcessing(
    auth.isLoggedIn,
    userId, 
    video.history, 
    video.setProgressModal,
    historyId
  );

  const globalCopy = useGlobalCopy(historySync.selectedHistory);

  // 9. UI 관련 훅들
  const swipe = useSwipeHandler(isMobile);

  const headerAnalyze = useHeaderAnalyze(
    video.loading, 
    video.progressModal, 
    globalCopy.globalCopyModal, 
    video.handleAnalyze, 
    globalCopy.handleGlobalCopyNavigation
  );

  const pageGuard = usePageGuard(
    userId, 
    historyId, 
    video.loading, 
    video.history, 
    historySync.selectedHistory, 
    video.isStreaming,
    backgroundReconnect
  );

  // 백그라운드 처리 중인 경우 진행률 모달 표시
  React.useEffect(() => {
    if (backgroundReconnect.backgroundProcessing && backgroundReconnect.existingAnalysis) {
      console.log('🔄 [BackgroundReconnect] 백그라운드 재연결 감지 - 진짜 ProgressModal 표시');
      
      // 🚨 GlobalCopyProgressModal이 열려있으면 강제로 닫기
      if (globalCopy.globalCopyModal.open) {
        console.log('🚫 [BackgroundReconnect] GlobalCopyProgressModal 강제 닫기');
        globalCopy.setGlobalCopyModal({ open: false, videoTitle: '' });
        
        // URL에서 잘못된 쿼리 파라미터 제거
        const currentUrl = new URL(window.location);
        if (currentUrl.searchParams.has('globalCopy')) {
          console.log('🧹 [BackgroundReconnect] URL에서 globalCopy 파라미터 제거');
          currentUrl.searchParams.delete('globalCopy');
          currentUrl.searchParams.delete('videoTitle');
          window.history.replaceState(null, '', currentUrl.pathname + currentUrl.search);
        }
      }
      
      const initialProgress = backgroundReconnect.existingAnalysis.processingProgress || 0;
      video.setProgressModal({
        open: true,
        currentMessage: video.getMessageByPercentage(initialProgress),
        progress: initialProgress,
        message: video.getMessageByPercentage(initialProgress),
        userId: userId,  // ✅ Firebase 리스너용 userId 추가
        historyId: historyId,  // ✅ Firebase 리스너용 historyId 추가
        onCancel: video.handleDirectCancel  // ✅ 버튼 표시를 위한 onCancel 핸들러 추가
      });
      
      // 시각적 진행률도 설정
      if (video.setVisualProgress) {
        video.setVisualProgress(initialProgress);
      }
      
      // 백그라운드 진행률 업데이트 전역 함수 설정
      window.updateBackgroundProgress = (progress) => {
        video.setProgressModal(prev => ({
          ...prev,
          progress: progress,
          currentMessage: video.getMessageByPercentage(progress),
          message: video.getMessageByPercentage(progress)
        }));
      };
      
      // cleanup
      return () => {
        delete window.updateBackgroundProgress;
      };
    }
  }, [backgroundReconnect.backgroundProcessing, video.getMessageByPercentage, userId, historyId, globalCopy.globalCopyModal.open, globalCopy.setGlobalCopyModal]);

  // 백그라운드 처리 완료 시 모달 완료 상태로 업데이트
  React.useEffect(() => {
    // 🚨 이미 처리했거나 조건이 맞지 않으면 return
    if (isCompletionHandled || 
        !backgroundReconnect.existingAnalysis?.status || 
        backgroundReconnect.existingAnalysis?.status !== 'active' || 
        backgroundReconnect.backgroundProcessing) {
      return;
    }

    console.log('🎉 [BackgroundReconnect] 분석 완료 - 모달 완료 상태로 업데이트');
    
    // ✅ 한 번만 실행되도록 플래그 설정
    setIsCompletionHandled(true);
    
    video.setProgressModal(prev => ({
      ...prev,
      progress: 100,
      currentMessage: t('analyze.analysisCompleteMessage'),
      userId: userId,
      historyId: historyId,
      onCancel: video.handleDirectCancel  // ✅ 완료 시에도 onCancel 유지 (확인 버튼으로 표시됨)
    }));
  }, [backgroundReconnect.existingAnalysis?.status, backgroundReconnect.backgroundProcessing, isCompletionHandled, t, userId, historyId]);

  // 백그라운드 처리가 다시 시작되면 플래그 리셋
  React.useEffect(() => {
    if (backgroundReconnect.backgroundProcessing && isCompletionHandled) {
      console.log('🔄 [BackgroundReconnect] 백그라운드 처리 재시작 - 완료 플래그 리셋');
      setIsCompletionHandled(false);
    }
  }, [backgroundReconnect.backgroundProcessing, isCompletionHandled]);

  // 에러 모달에서 이동해온 경우 진행률 모달 표시
  React.useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const fromError = urlParams.get('fromError');
    
    if (fromError === 'true' && historySync.selectedHistory && userId) {
      console.log('🔄 [Error Navigation] 에러 모달에서 이동 - 진행률 모달 표시');
      
      // URL 파라미터 정리
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('fromError');
      window.history.replaceState(null, '', newUrl.pathname + newUrl.search);
      
      // 현재 히스토리 상태 확인
      const currentHistory = historySync.selectedHistory;
      const currentProgress = currentHistory.processingProgress || 0;
      
      if (currentHistory.status === 'analyzing' || currentHistory.backgroundProcessing) {
        // 분석 중인 경우 진행률 모달 표시
        video.setProgressModal({
          open: true,
          currentMessage: video.getMessageByPercentage(currentProgress),
          progress: currentProgress,
          message: video.getMessageByPercentage(currentProgress),
          userId: userId,
          historyId: historyId,
          onCancel: video.handleDirectCancel
        });
      } else if (currentHistory.status === 'active') {
        // 완료된 경우 완료 모달 표시
        video.setProgressModal({
          open: true,
          currentMessage: t('analyze.analysisCompleteMessage'),
          progress: 100,
          message: t('analyze.analysisCompleteMessage'),
          userId: userId,
          historyId: historyId,
          onCancel: video.handleDirectCancel
        });
      }
    }
  }, [location.search, historySync.selectedHistory, userId, historyId, video.setProgressModal, video.getMessageByPercentage, video.handleDirectCancel, t]);

  // 통합 반환
  return {
    // 기본 설정
    historyId,
    navigate,
    theme,
    activeTab,
    setActiveTab,
    t,
    i18n,
    isMobile,
    
    // 계산된 상태들
    ...computed,

    // 훅 결과들
    auth,
    video,
    historySync,
    translation,
    transcript,
    qa,
    analysis,
    resume,
    globalCopy,
    swipe,
    headerAnalyze,
    pageGuard,
    backgroundReconnect
  };
}; 